import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, Send, Loader2, Instagram } from "lucide-react";
import { EVENT_TYPES, PACKAGES, WHATSAPP_NUMBERS, EMAIL, INSTAGRAM_URL, whatsappLink } from "@/data/content";
import { Eyebrow, Feather } from "./Ornaments";
import { Reveal } from "./Reveal";
import { Field } from "./Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", event_type: "", event_date: "", city: "", guests: "", budget: "", package: "", message: "" };

export const Enquiry = ({ preset }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!preset) return;
    const isPkg = PACKAGES.some((p) => p.name === preset);
    setForm((f) => (isPkg ? { ...f, package: preset } : { ...f, event_type: preset }));
  }, [preset]);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! Our planner will reach out within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your email and phone details." : "Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-24 sm:py-32 bg-blush jaali">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Customer enquiry</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05]">Let's plan something <span className="italic text-terra">beautiful.</span></h2>
          <p className="text-base md:text-lg text-muted-ink mt-6 leading-relaxed">Share a few details and a Nexora planner will call you with ideas, vendor options and a clear estimate.</p>
          <div className="mt-10 rounded-[28px] bg-cream p-6 sm:p-8 border border-gold/30 relative overflow-hidden">
            <Feather className="absolute -right-1 -top-2 w-10 h-28 opacity-70 sway" />
            <p className="font-display text-2xl text-ink">Prefer to chat?</p>
            <p className="text-sm text-muted-ink mt-2">Message us on WhatsApp — we usually reply within the hour.</p>
            <a data-testid="enquiry-whatsapp-button" href={whatsappLink()} target="_blank" rel="noreferrer" className="btn-sage mt-6">
              <MessageCircle size={18} /> WhatsApp us
            </a>
            <div className="mt-5 space-y-1.5">
              {WHATSAPP_NUMBERS.map((w, i) => (
                <p key={w.num} className="flex items-center gap-2 text-sm text-muted-ink">
                  <Phone size={14} /> <a href={`tel:+${w.num}`} data-testid={`enquiry-phone-${i}`} className="hover:text-terra-deep transition-colors">{w.disp}</a>
                </p>
              ))}
              <p className="flex items-center gap-2 text-sm text-muted-ink"><Mail size={14} /> <a href={`mailto:${EMAIL}`} data-testid="enquiry-email-link" className="hover:text-terra-deep transition-colors">{EMAIL}</a></p>
            </div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="enquiry-instagram-button" className="btn-ghost mt-5">
              <Instagram size={17} /> @nexoraevents.in
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="enquiry-form" className="bg-white rounded-[32px] p-6 sm:p-10 border border-terra/10 shadow-[0_40px_80px_-50px_rgba(64,52,52,0.4)] grid sm:grid-cols-2 gap-5">
            <Field label="Your name" name="name" required value={form.name} onChange={set} testid="enquiry-name-input" />
            <Field label="Phone / WhatsApp" name="phone" type="tel" required value={form.phone} onChange={set} testid="enquiry-phone-input" />
            <Field label="Email" name="email" type="email" required value={form.email} onChange={set} testid="enquiry-email-input" />
            <Field label="Occasion" name="event_type" as="select" options={EVENT_TYPES} required value={form.event_type} onChange={set} testid="enquiry-event-type-select" />
            <Field label="Event date" name="event_date" type="date" value={form.event_date} onChange={set} testid="enquiry-date-input" />
            <Field label="City / destination" name="city" value={form.city} onChange={set} placeholder="e.g. Jaipur" testid="enquiry-city-input" />
            <Field label="Guests" name="guests" as="select" options={["Under 50", "50–150", "150–400", "400+"]} value={form.guests} onChange={set} testid="enquiry-guests-select" />
            <Field label="Package" name="package" as="select" options={PACKAGES.map((p) => p.name).concat(["Not sure yet"])} value={form.package} onChange={set} testid="enquiry-package-select" />
            <div className="sm:col-span-2">
              <Field label="Budget range" name="budget" as="select" options={["Under ₹5 Lakh", "₹5–15 Lakh", "₹15–40 Lakh", "₹40 Lakh+", "Flexible"]} value={form.budget} onChange={set} testid="enquiry-budget-select" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Tell us your vision" name="message" as="textarea" value={form.message} onChange={set} placeholder="Colours, rituals, vibe, must-haves…" testid="enquiry-message-input" />
            </div>
            <button type="submit" disabled={loading} data-testid="enquiry-submit-button" className="btn-pink sm:col-span-2 justify-center py-4 text-base disabled:opacity-70">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Send my enquiry
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
